import apisauce, {
    CancelToken as CancelTokenSource,
} from 'apisauce'
import {Coordinates, ReminderSettings, ServiceDateParams} from '../interfaces'
import {getApiKeyHeaders} from '../Utils/hooks'
import {getLocale} from '../Utils/helpers'

const _CancelToken = CancelTokenSource.source()
type CancelToken = typeof _CancelToken.token

const create = () => {
    if (process.env.NODE_ENV === 'development') {
        if (!process.env.REACT_APP_API_PORT)
            throw Error('Missing REACT_APP_API_PORT environment variable')
        if (!process.env.REACT_APP_LOCALHOST_URL)
            throw Error('Missing REACT_APP_LOCALHOST_URL environment variable')
    }
    const baseURL =
        process.env.NODE_ENV === 'development'
            ? `https://${process.env.REACT_APP_LOCALHOST_URL}:${process.env.REACT_APP_API_PORT}`
            : `${window.location.origin}/api`
    const api = apisauce.create({
        baseURL,
        timeout: 30000,
        headers: getApiKeyHeaders(),
    })
    api.addRequestTransform((request) => {
        request.headers = request.headers || {}
        request.headers['Accept-Language'] = getLocale()
    })
    return {
        postError: async (error: any, url: any) => {
            const data = {
                url,
                error: JSON.stringify(error?.error ?? {}),
                message: (error?.message ?? '-') + ';' + (error?.error?.message ?? '-'),
                trace: error?.stack ?? '-',
                stack: error?.error?.stack ?? '-',
                userAgent: error?.userAgent ?? '?',
                info: JSON.stringify(Object.getOwnPropertyNames(error ?? {})),
            }

            return process.env.NODE_ENV === 'development' ? null : await api.post('log/error', data)
        },
        postFinalReservation: async (reservation: any) =>
            await api.post('final-reservation', reservation),
        postTrackingInfo: async ({chainId, stationId, event}: any) =>
            await api.post(`chains/${chainId}/stations/${stationId}/track/event`, event),
        getSettings: async (chainId: number, stationId: number | null, embedded: boolean) =>
            await api.get(
                `settings/chain/${chainId}${
                    stationId ? '/station/' + stationId : ''
                }${embedded ? '?embed' : ''}`,
            ),
        getChainSettingsByToken: async (token: string, embedded: boolean) =>
            await api.get(`settings/token/${token}/chain${embedded ? '?embed' : ''}`),
        getStationSettingsByToken: async (token: string, embedded: boolean) =>
            await api.get(`settings/token/${token}/station${embedded ? '?embed' : ''}`),
        postReservation: async (reservation: any) =>
            await api.post('reservation', reservation),
        putReservation: async (reservation: any) =>
            await api.put('reservation', reservation),
        discardReservation: async (reservation: any) =>
            await api.put('discard-reservation', reservation),
        getCustomerInfo: async (chainId, stationId, customerToken: string) =>
            await api.get(`/chains/${chainId}/${stationId ? `stations/${stationId}/` : ''}customers/${customerToken}`),
        getRegisterInfo: async ({ customerToken, registerNumber, vehicleType, vehicleClass, vehicleGroup, engineType, searchType, stationId, chainId }: any) =>
            await api.post(
                `/chains/${chainId}/stations/${stationId}/vehicles/search`,
                { customerToken, registrationNumber: registerNumber, vehicleType, vehicleClass, vehicleGroup, engineType, source: searchType }
            ),
        getStations: async (
            {
                coordinates,
                chainId,
                filter,
                stationId,
            }: {
                coordinates?: Coordinates
                chainId: number | string
                filter?: string
                stationId?: number | string
            },
            cancelToken: CancelToken,
        ) =>
            await api.get(
                `chain/${chainId}/stations${stationId ? '/' + stationId : ''}`,
                { filter, ...coordinates },
                {
                    cancelToken,
                },
            ),
        getServices: async (
            {
                stationId,
                vehicleGroupId,
                campaign,
                priceType,
                fuel,
                customer,
                chainId,
            }: {
                stationId: number
                vehicleGroupId: number
                campaign: string | null
                priceType: number | null
                fuel: string | null,
                customer: string | null,
                chainId: string | number
            },
            cancelToken: CancelToken,
        ) => {
            return await api.get(
                `chains/${chainId}/stations/${stationId}/services`,
                { campaign, fuel, vehicleGroupId, priceType, customer },
                { cancelToken },
            )
        },
        getDates: async ({
            dateParams,
            chainId,
        }: {
            dateParams: ServiceDateParams
            chainId: number
        }) => await api.get(`chain/${chainId}/service-dates`, dateParams),
        getBestPriceDates: async ({
            dateParams,
            chainId,
        }: {
            dateParams: ServiceDateParams
            chainId: number
        }) =>
            await api.get(
                `chain/${chainId}/service-dates/best-prices`,
                dateParams,
            ),
        getGeocodes: async (address: string) =>
            await api.get('geocode', { address }),
        getCheckIn: async (token: string) =>
            await api.get(`service-card/${token}`),
        cancelReservation: async (token: string) =>
            await api.put(`service-card/cancel/${token}`),
        checkIn: async (token: string, card: {token: string, customerToken?: string}) =>
            await api.put(`service-card/check-in/${token}`, card),
        changeReservation: async ({
            token,
            modifications,
        }: {
            token: string
            modifications: any
        }) => await api.put(`service-card/${token}`, modifications),
        createPayment: async ({
            token,
            quick,
            email,
        }: {
            token: string
            quick?: boolean
            email?: string
        }) => await api.post(`online-payment/create/${token}`, { quick, email }),
        handlePaymentResult: async ({
            result,
            query,
        }: {
            result: any
            query: string
        }) => await api.get(`online-payment/result/${result}${query}`),
        cancelPayment: async ({
            token,
        }: {
            token: string
        }) => await api.post(`online-payment/cancel/${token}`, { }),
        confirmReservationChanges: async ({ token }: { token: string }) =>
            await api.post(`service-card/confirm/${token}`),
        revertReservationChanges: async ({ token }: { token: string }) =>
            await api.post(`service-card/revert/${token}`),
        giveFeedback: async ({ token, feedbackText, grade, gradeOffice, wantsResponse, email }: any) =>
            await api.post(`service-card/feedback/${token}`, {
                feedbackText,
                grade,
                gradeOffice,
                wantsResponse,
                email,
            }),
        postReminderSettings: async (chainId: number, stationId: number, settings: ReminderSettings) =>
            await api.post(`chains/${chainId}/stations/${stationId}/reminder/settings`, settings),
        getReceipt: async (token: string) =>
            await api.get(`service-card/${token}/receipt`, undefined, {
                responseType: 'arraybuffer',
            }),
        getCertificate: async (token: string) =>
            await api.get(`service-card/${token}/certificate`, undefined, {
                responseType: 'arraybuffer',
            }),
        getCertificateWithCode: async (code: string) =>
            await api.get(`service-card/certificate/${code}`, undefined, {
                responseType: 'arraybuffer',
            }),
        getReservationWithRegistrationNumber: async (chainId: number, stationId: number, registrationNumber: string, customerToken?: string) =>
            await api.get(`chains/${chainId}/stations/${stationId}/reservations/registrationNumber/${registrationNumber.length > 0 ? registrationNumber : '-'}${customerToken ? `?customer=${customerToken}` : ''}`),
        postLogin: async (systemName: string, systemKey: string) =>
            await api.post('login', {systemName: systemName, systemKey: systemKey}),
        postCreateSpecial: async (chainId: number, stationId: number, type: string, data: any)=>
            await api.post(`chains/${chainId}/stations/${stationId}/reservations/${type}`, data),
        postFindAvailableTimeSpecial: async (chainId: number, stationId: number, type: string)=>
            await api.post(`chains/${chainId}/stations/${stationId}/reservations/${type}/check`),
        postPrintReceipt: async (token: string, type: string) =>
            await api.post(`service-card/${token}/print/${type}`),
        postCreateCardPayment: async (token: string) =>
            await api.post(`card-payment/create/${token}`),
        postCardPaymentResult: async (token: string, transactionResult: any) =>
            await api.post(`card-payment/result/${token}`, transactionResult),
        getNexiJwt: async (token: string) =>
            await api.get(`card-payment/nexi/jwt/${token}`),
        postCreateNexiCardPayment: async (token: string) =>
            await api.post(`card-payment/nexi/create/${token}`),
        postNexiCardPaymentResult: async (token: string, transactionId: number, body?: any) =>
            await api.post(`card-payment/nexi/result/${token}/${transactionId}`, body),
    }
}

export type ApiType = ReturnType<typeof create>

export default {
    create,
}
